class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      if (this.closePhotoPreview()) {
        evt.stopPropagation();
        return;
      }
      this.close();
    });
    this.addEventListener('click', this.handlePhotoPreviewClick.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  handlePhotoPreviewClick(event) {
    const previewLink = event.target.closest('[data-cart-photo-preview-link]');
    if (previewLink) {
      event.preventDefault();
      this.openPhotoPreview(previewLink);
      return;
    }

    if (event.target.closest('[data-cart-photo-preview-close]')) {
      this.closePhotoPreview();
    }
  }

  openPhotoPreview(previewLink) {
    const preview = this.querySelector('[data-cart-photo-preview]');
    const image = preview?.querySelector('[data-cart-photo-preview-image]');
    const originalLink = preview?.querySelector('[data-cart-photo-preview-original]');
    const closeButton = preview?.querySelector('[data-cart-photo-preview-close]');
    if (!preview || !image || !originalLink || !closeButton) return;

    this.photoPreviewTrigger = previewLink;
    image.src = previewLink.href;
    image.alt = previewLink.dataset.photoLabel || 'Uploaded pet photo';
    originalLink.href = previewLink.href;
    preview.hidden = false;
    trapFocus(preview, closeButton);
  }

  closePhotoPreview(restoreFocus = true) {
    const preview = this.querySelector('[data-cart-photo-preview]');
    if (!preview || preview.hidden) return false;

    const image = preview.querySelector('[data-cart-photo-preview-image]');
    preview.hidden = true;
    image?.removeAttribute('src');

    const trigger = this.photoPreviewTrigger;
    this.photoPreviewTrigger = null;
    removeTrapFocus();

    if (restoreFocus && trigger?.isConnected) {
      trapFocus(document.getElementById('CartDrawer'), trigger);
    }

    return true;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.closePhotoPreview(false);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

const cartOpenUrl = new URL(window.location.href);

if (cartOpenUrl.searchParams.get('cart') === 'open') {
  cartOpenUrl.searchParams.delete('cart');
  window.history.replaceState(
    window.history.state,
    '',
    `${cartOpenUrl.pathname}${cartOpenUrl.search}${cartOpenUrl.hash}`,
  );

  document.querySelector('cart-drawer')?.open(document.querySelector('#cart-icon-bubble'));
}
